
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { UserActivityService } from "@/services/userActivityService";
import { DeviceRegistrationService } from "@/services/deviceRegistrationService";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Handle activity tracking and device registration based on auth events
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('🎯 User signed in, starting activity tracking and device registration');
          setTimeout(() => {
            UserActivityService.startActivityTracking(session.user.id);
            DeviceRegistrationService.registerCurrentDevice(session.user.id);
            DeviceRegistrationService.setupTokenRefreshListener(session.user.id);
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          console.log('👋 User signed out, stopping activity tracking');
          UserActivityService.stopActivityTracking();
          DeviceRegistrationService.removeTokenRefreshListener();
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          console.log('🔄 Token refreshed, updating activity');
          setTimeout(() => {
            UserActivityService.updateActivityOnAction(session.user.id);
          }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session check:', session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Start activity tracking and device registration for existing session
      if (session?.user) {
        console.log('🎯 Existing session found, starting activity tracking and device registration');
        UserActivityService.startActivityTracking(session.user.id);
        DeviceRegistrationService.registerCurrentDevice(session.user.id);
        DeviceRegistrationService.setupTokenRefreshListener(session.user.id);
      }
    });

    return () => {
      subscription.unsubscribe();
      UserActivityService.stopActivityTracking();
      DeviceRegistrationService.removeTokenRefreshListener();
    };
  }, []);

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
    // Clear local state immediately
    setSession(null);
    setUser(null);
    UserActivityService.stopActivityTracking();
    DeviceRegistrationService.removeTokenRefreshListener();
  };

  return { user, session, loading, signOut };
};
